/* WATCHERS V3 — CART DRAWER — CD20 QUEUED CONFIRMED MULTI-REMOVE */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.bindOverlay();
      this.setHeaderCartIconAccessibility();
    }

    bindOverlay() {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (!overlay) return;
      overlay.onclick = (event) => {
        event.preventDefault();
        this.close();
      };
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink || cartLink.dataset.watchersCartBound === 'true') return;

      cartLink.dataset.watchersCartBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    lockViewport() {
      document.body.classList.add('overflow-hidden');
      document.documentElement.style.setProperty('overflow-x', 'hidden');
      document.body.style.setProperty('overflow-x', 'hidden');
      document.documentElement.scrollLeft = 0;
      document.body.scrollLeft = 0;
    }

    releaseViewport() {
      document.body.classList.remove(
        'overflow-hidden',
        'overflow-hidden-mobile',
        'overflow-hidden-tablet',
        'overflow-hidden-desktop'
      );

      document.documentElement.classList.remove(
        'overflow-hidden',
        'overflow-hidden-mobile',
        'overflow-hidden-tablet',
        'overflow-hidden-desktop'
      );

      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow-x');
      document.documentElement.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow-x');
      document.documentElement.scrollLeft = 0;
      document.body.scrollLeft = 0;
    }

    setEmptyState(isEmpty) {
      this.classList.toggle('is-empty', Boolean(isEmpty));
      this.querySelector('.drawer__inner')?.classList.toggle('is-empty', Boolean(isEmpty));
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);

      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
        this.setSummaryAccessibility(cartDrawerNote);
      }

      this.bindOverlay();
      this.lockViewport();

      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        () => {
          if (!this.classList.contains('active')) return;

          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');

          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          if (containerToTrapFocusOn && focusElement) {
            try { trapFocus(containerToTrapFocusOn, focusElement); } catch (_) {}
          }
        },
        { once: true }
      );
    }

    close() {
      this.classList.remove('active', 'animate');
      try { removeTrapFocus(this.activeElement); } catch (_) {}
      this.releaseViewport();
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute(
          'aria-expanded',
          !event.currentTarget.closest('details').hasAttribute('open')
        );
      });

      cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
    }

    async primeCartImages(timeout = 90) {
      const images = Array.from(
        this.querySelectorAll(
          '.watchers-cart__item-image, .cart-item__image'
        )
      );

      if (!images.length) return;

      /*
        Keep cart images lazy during normal storefront browsing.
        Only when the customer is actually opening the bag do we promote
        those images to eager/high priority.
      */
      images.forEach((img) => {
        img.loading = 'eager';

        try {
          img.fetchPriority = 'high';
        } catch (_) {}

        /*
          Reading currentSrc after promotion encourages the browser to start
          the request immediately without changing the Liquid image URL.
        */
        void img.currentSrc;
      });

      const waits = images.map((img) => {
        if (img.complete && img.naturalWidth > 0) {
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          let settled = false;

          const done = () => {
            if (settled) return;
            settled = true;
            resolve();
          };

          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });

          if (typeof img.decode === 'function') {
            img.decode().then(done).catch(() => {});
          }
        });
      });

      await Promise.race([
        Promise.all(waits),
        new Promise((resolve) => window.setTimeout(resolve, timeout)),
      ]);
    }

    renderContents(parsedState) {
      this.productId = parsedState?.id;

      /*
        CD04 — ATOMIC WATCHERS CART RENDER

        Replace the entire contents of the persistent <cart-drawer> from the
        freshly-rendered Shopify cart-drawer section. Do not update only
        #CartDrawer, because an empty-state shell from a previous render can
        otherwise survive around a newly-populated drawer.
      */
      const drawerSectionHTML = parsedState?.sections?.['cart-drawer'];

      if (drawerSectionHTML) {
        const parsedDocument = new DOMParser().parseFromString(
          drawerSectionHTML,
          'text/html'
        );

        const freshDrawer = parsedDocument.querySelector('cart-drawer');

        if (freshDrawer) {
          this.innerHTML = freshDrawer.innerHTML;
        } else {
          const freshCartDrawer = parsedDocument.querySelector('#CartDrawer');
          const currentCartDrawer = this.querySelector('#CartDrawer');

          if (freshCartDrawer && currentCartDrawer) {
            currentCartDrawer.replaceWith(
              document.importNode(freshCartDrawer, true)
            );
          }
        }
      }

      const bubbleHTML = parsedState?.sections?.['cart-icon-bubble'];
      const currentBubble = document.getElementById('cart-icon-bubble');

      if (bubbleHTML && currentBubble) {
        const parsedBubble = new DOMParser().parseFromString(
          bubbleHTML,
          'text/html'
        );

        const freshBubble =
          parsedBubble.querySelector('#cart-icon-bubble') ||
          parsedBubble.querySelector('.shopify-section #cart-icon-bubble');

        if (freshBubble) {
          currentBubble.replaceWith(document.importNode(freshBubble, true));
          this.setHeaderCartIconAccessibility();
        }
      }

      /* Remove any accidental stale duplicate drawer root. */
      document.querySelectorAll('cart-drawer').forEach((drawer) => {
        if (drawer !== this) drawer.remove();
      });

      let isEmpty = false;

      if (typeof parsedState?.item_count === 'number') {
        isEmpty = parsedState.item_count === 0;
      } else {
        isEmpty = Boolean(
          this.querySelector('.drawer__inner-empty, .watchers-cart__empty')
        );
      }

      this.setEmptyState(isEmpty);
      this.bindOverlay();

      document.dispatchEvent(
        new CustomEvent('watchers:cart:rendered', {
          detail: { itemCount: Number(parsedState?.item_count || 0) }
        })
      );

      if (parsedState?.watchersOpenDrawer !== false) {
        /*
          CD20 — FAST ADD DRAWER OPEN

          Promote the freshly-rendered cart images immediately, but do not hold
          the drawer open waiting for decode/network. The promotion runs before
          the next frame, so cached images can paint immediately while the cart
          itself opens without the previous 90ms gate.
        */
        this.primeCartImages(0);
        requestAnimationFrame(() => this.open());
      }
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parsedDocument = new DOMParser().parseFromString(html || '', 'text/html');
      const element = parsedDocument.querySelector(selector);
      return element ? element.innerHTML : '';
    }

    getSectionsToRender() {
      return [
        { id: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble' },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}

document.addEventListener('watchers:navigation:complete', () => {
  const drawer = document.querySelector('cart-drawer');
  if (drawer && !drawer.classList.contains('active') && typeof drawer.releaseViewport === 'function') {
    drawer.releaseViewport();
  }
});


/*
  CD05 — GLOBAL WATCHERS CART OWNER

  The cart icon is an <a href="/cart">. When Shopify replaces the cart-icon
  section, any element-level click listener can disappear for a moment. A
  capture-phase document listener survives every replacement and guarantees
  that the cart icon always opens the WATCHERS drawer instead of navigating to
  the old full cart page.
*/
(() => {
  if (window.__WATCHERS_GLOBAL_CART_OWNER__) return;
  window.__WATCHERS_GLOBAL_CART_OWNER__ = true;

  const getDrawer = () =>
    document.querySelector('cart-drawer.watchers-cart') ||
    document.querySelector('cart-drawer');

  const openWatchersDrawer = (trigger) => {
    const drawer = getDrawer();
    if (!drawer || typeof drawer.open !== 'function') return false;

    const doOpen = () => {
      try {
        drawer.open(trigger || document.querySelector('#cart-icon-bubble'));
      } catch (error) {
        console.error('[WATCHERS Cart Owner]', error);
      }
    };

    /*
      Manual cart-icon opening gets the same image warmup as Add to Bag.
      Do not preload the hidden cart during normal browsing.
    */
    if (typeof drawer.primeCartImages === 'function') {
      drawer.primeCartImages(90).finally(() => {
        requestAnimationFrame(doOpen);
      });
    } else {
      requestAnimationFrame(doOpen);
    }

    return true;
  };

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target?.closest?.('#cart-icon-bubble');
      if (!trigger) return;

      const drawer = getDrawer();
      if (!drawer || typeof drawer.open !== 'function') return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      openWatchersDrawer(trigger);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      const trigger = event.target?.closest?.('#cart-icon-bubble');
      if (!trigger) return;

      if (event.key !== 'Enter' && event.key !== ' ') return;

      const drawer = getDrawer();
      if (!drawer || typeof drawer.open !== 'function') return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      openWatchersDrawer(trigger);
    },
    true
  );

  /*
    Persistent navigation can replace MainContent while header/cart drawer stay
    mounted. Re-assert the drawer's viewport state after every WATCHERS swap.
  */
  document.addEventListener('watchers:navigation:complete', () => {
    const drawer = getDrawer();
    if (!drawer) return;

    if (
      !drawer.classList.contains('active') &&
      typeof drawer.releaseViewport === 'function'
    ) {
      drawer.releaseViewport();
    }
  });

  window.addEventListener('pageshow', () => {
    const drawer = getDrawer();
    if (
      drawer &&
      !drawer.classList.contains('active') &&
      typeof drawer.releaseViewport === 'function'
    ) {
      drawer.releaseViewport();
    }
  });
})();


/*
  CD06 — GLOBAL PDP ADD-TO-BAG OWNER

  Persistent navigation can arrive on a product page after an older
  `product-form` custom element has already been registered. Because custom
  elements cannot be redefined, the newly-loaded product-form.js may never own
  that PDP.

  Capture the product-form submit at document level BEFORE any old form listener
  runs. This makes PDP Add to Bag independent of whichever ProductForm class was
  registered first in the browser session.
*/
(() => {
  if (window.__WATCHERS_GLOBAL_PDP_ADD_OWNER__) return;
  window.__WATCHERS_GLOBAL_PDP_ADD_OWNER__ = true;

  const getDrawer = () =>
    document.querySelector('cart-drawer.watchers-cart') ||
    document.querySelector('cart-drawer');

  const getCurrentCart = async () => {
    const response = await fetch(
      `${window.routes?.cart_url || '/cart'}.js?_watchers=${Date.now()}`,
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        cache: 'no-store',
      }
    );

    if (!response.ok) {
      throw new Error('Unable to read the current bag.');
    }

    return response.json();
  };

  const getVariantQuantityInCart = (cart, variantId) =>
    (cart?.items || [])
      .filter((item) => String(item.variant_id) === String(variantId))
      .reduce((total, item) => total + Number(item.quantity || 0), 0);

  const getAssociatedQuantityInput = (form) => {
    if (!form) return null;

    const fromElements = form.elements?.namedItem?.('quantity');

    if (fromElements instanceof HTMLInputElement) {
      return fromElements;
    }

    const inside = form.querySelector('[name="quantity"]');
    if (inside) return inside;

    if (form.id) {
      return document.querySelector(
        `input[name="quantity"][form="${CSS.escape(form.id)}"]`
      );
    }

    return null;
  };

  const recoverProductForm = (productForm, form) => {
    if (!productForm) return;

    productForm.dataset.watchersSubmitting = 'false';

    const button = productForm.querySelector('[type="submit"]');
    if (!button) return;

    button.removeAttribute('aria-disabled');
    button.classList.remove('loading');

    productForm.querySelector('.loading__spinner')?.classList.add('hidden');

    const soldOutMessage = button.querySelector('.sold-out-message');
    const soldOutVisible =
      soldOutMessage && !soldOutMessage.classList.contains('hidden');

    if (!soldOutVisible) {
      button.removeAttribute('disabled');
    }

    const quantityInput = getAssociatedQuantityInput(form);
    if (quantityInput) {
      quantityInput.removeAttribute('aria-invalid');
      quantityInput.setCustomValidity?.('');
    }
  };

  const setButtonBusy = (productForm, busy) => {
    const button = productForm?.querySelector('[type="submit"]');
    if (!button) return;

    if (busy) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      productForm.querySelector('.loading__spinner')?.classList.remove('hidden');
    } else {
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      productForm.querySelector('.loading__spinner')?.classList.add('hidden');
    }
  };

  const showError = (productForm, message = '') => {
    const wrapper = productForm?.querySelector(
      '.product-form__error-message-wrapper'
    );
    const text = wrapper?.querySelector(
      '.product-form__error-message'
    );

    if (!wrapper || !text) return;

    wrapper.toggleAttribute('hidden', !message);
    if (message) text.textContent = message;
  };

  const getVariantMap = (form) => {
    try {
      const productInfo =
        form.closest('product-info') ||
        document.querySelector('product-info');

      const mapNode =
        productInfo?.querySelector(
          'script[data-watchers-variant-inventory-map]'
        );

      if (!mapNode?.textContent) return [];

      const parsed = JSON.parse(mapNode.textContent);

      return Array.isArray(parsed)
        ? parsed
        : [];
    } catch (error) {
      console.warn(
        '[WATCHERS Variant Map]',
        error
      );

      return [];
    }
  };


  const getLiveSelectedOptionValues = (form) => {
    const productInfo =
      form.closest('product-info') ||
      document.querySelector('product-info');

    if (!productInfo) return [];

    /*
      Shopify/Dawn can update the visible option controls before the hidden
      product-form variant ID finishes refreshing.

      Read what the customer has ACTUALLY selected on screen.
    */
    const values = [];

    const selects =
      productInfo.querySelectorAll(
        'variant-selects select, ' +
        'select[name^="options"]'
      );

    selects.forEach((select) => {
      const value = String(select.value || '').trim();
      if (value) values.push(value);
    });

    if (values.length) {
      return values;
    }

    /*
      Dawn's current variant picker uses <variant-selects> and reads selected
      values from `fieldset input:checked`. The radio name is NOT guaranteed to
      start with "options", so the old selector could miss the newly selected
      size and fall back to the PREVIOUS hidden variant ID.

      Read every checked radio/pill inside the active variant-selects element.
    */
    const checked =
      productInfo.querySelectorAll(
        'variant-selects fieldset input[type="radio"]:checked, ' +
        'variant-selects input[type="radio"]:checked, ' +
        'fieldset input[type="radio"][data-option-value-id]:checked'
      );

    checked.forEach((input) => {
      const value = String(input.value || '').trim();
      if (value) values.push(value);
    });

    return values;
  };


  const getExactLiveSelectedVariantId = (
    form,
    fallbackVariantId
  ) => {
    const variants =
      getVariantMap(form);

    const optionValues =
      getLiveSelectedOptionValues(form);

    if (
      variants.length &&
      optionValues.length
    ) {
      const matched =
        variants.find((variant) => {
          if (!Array.isArray(variant?.options)) {
            return false;
          }

          if (
            variant.options.length !==
            optionValues.length
          ) {
            return false;
          }

          return variant.options.every(
            (value, index) =>
              String(value || '').trim() ===
              String(optionValues[index] || '').trim()
          );
        });

      if (matched?.id) {
        return String(matched.id);
      }
    }

    return String(
      fallbackVariantId || ''
    );
  };


  const getExactSelectedVariantInventory = (
    form,
    selectedVariantId,
    quantityInput
  ) => {
    /*
      CD22 — EXACT VARIANT INVENTORY, SURGICAL ON TOP OF CD20

      CD20 already counts cart quantity by exact variant ID.
      The bug was that `data-watchers-available` on the quantity input can be
      stale after switching size, because Dawn does not necessarily refresh
      custom quantity-input data attributes during its variant update.

      Use the exact selected variant ID being submitted against MP05's static
      per-variant inventory map. Fall back to the original CD20 DOM attribute
      only if the map is unavailable.
    */
    const variants =
      getVariantMap(form);

    const selected =
      variants.find(
        (variant) =>
          String(variant?.id || '') ===
          String(selectedVariantId || '')
      );

    if (selected) {
      if (
        selected.inventory_management !== 'shopify' ||
        selected.inventory_policy === 'continue'
      ) {
        return null;
      }

      const inventory =
        Number(selected.inventory_quantity);

      if (Number.isFinite(inventory)) {
        return Math.max(
          0,
          Math.floor(inventory)
        );
      }
    }

    const raw =
      quantityInput?.dataset?.watchersAvailable;

    if (
      raw === undefined ||
      raw === ''
    ) {
      return null;
    }

    const fallback = Number(raw);

    return Number.isFinite(fallback)
      ? Math.max(0, Math.floor(fallback))
      : null;
  };


  const updateCartBubbleFromServer = async () => {
    try {
      const response = await fetch(
        `${window.routes?.cart_url || '/cart'}.js?_watchers=${Date.now()}`,
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          cache: 'no-store',
        }
      );

      if (!response.ok) return;

      const cart = await response.json();
      const count = Number(cart?.item_count || 0);
      const trigger = document.querySelector('#cart-icon-bubble');

      if (!trigger) return;

      let bubble = trigger.querySelector('.cart-count-bubble');

      if (count <= 0) {
        bubble?.remove();
        return;
      }

      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        trigger.appendChild(bubble);
      }

      bubble.innerHTML =
        count < 100
          ? `<span aria-hidden="true">${count}</span><span class="visually-hidden">${count} item${count === 1 ? '' : 's'}</span>`
          : `<span class="visually-hidden">${count} items</span>`;
    } catch (error) {
      console.warn('[WATCHERS PDP Cart Bubble]', error);
    }
  };

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const productForm = form.closest('product-form');
      if (!productForm) return;

      /*
        Do not take ownership of Shopify payment/installment forms or unrelated
        forms. The actual Add to Bag product form always has a variant id field
        plus a submit button.
      */
      const variantInput = form.querySelector('[name="id"]');
      const submitButton = productForm.querySelector('[type="submit"]');

      if (!variantInput || !submitButton) return;
      if (productForm.closest('quick-add-modal')) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (productForm.dataset.watchersSubmitting === 'true') return;
      productForm.dataset.watchersSubmitting = 'true';

      showError(productForm, '');
      setButtonBusy(productForm, true);

      try {
        const drawer = getDrawer();

        if (!drawer || typeof drawer.renderContents !== 'function') {
          throw new Error('WATCHERS bag is not ready.');
        }

        const formData = new FormData(form);

        const hiddenVariantId =
          String(
            formData.get('id') ||
            variantInput.value ||
            ''
          );

        const selectedVariantId =
          getExactLiveSelectedVariantId(
            form,
            hiddenVariantId
          );

        /*
          CD24 — STRICT VARIANT SEPARATION

          Different Shopify variant IDs are different cart items for max-qty.
          If the visible selected size resolves to a different variant ID than
          Dawn's temporarily stale hidden input, ALWAYS use the visible variant
          ID for:
          - cart quantity lookup
          - inventory lookup
          - /cart/add.js submission
        */

        /*
          CD23:
          Make the submitted variant ID match the live size/options the customer
          selected, even if Dawn's hidden input is one render behind.
        */
        if (selectedVariantId) {
          formData.set(
            'id',
            selectedVariantId
          );

          if (
            String(variantInput.value || '') !==
            selectedVariantId
          ) {
            variantInput.value =
              selectedVariantId;
          }
        }

        /*
          CD14 — CART-AWARE INVENTORY
          `data-watchers-available` is the variant's sellable inventory, not
          the remaining quantity after items already in the cart. Read the
          current cart so PDP messaging can distinguish:
            - inventory exists, but it is already all in the bag
            - shopper typed a quantity above total inventory
        */
        let currentCart = null;
        let alreadyInCart = 0;

        try {
          currentCart = await getCurrentCart();
          alreadyInCart = getVariantQuantityInCart(
            currentCart,
            selectedVariantId
          );
        } catch (cartReadError) {
          console.warn('[WATCHERS PDP Cart Read]', cartReadError);
        }

        /*
          Clamp only against limits already present in the PDP DOM.
          Shopify remains the final inventory authority.
        */
        const quantityInput = getAssociatedQuantityInput(form);

        if (quantityInput) {
          let quantity = Number(quantityInput.value || 1);
          const min = Number(quantityInput.min || quantityInput.dataset.min || 1);
          const maxRaw = quantityInput.max || quantityInput.dataset.max || '';
          const max = maxRaw === '' ? null : Number(maxRaw);

          const available =
            getExactSelectedVariantInventory(
              form,
              selectedVariantId,
              quantityInput
            );

          if (!Number.isFinite(quantity)) quantity = 1;
          if (Number.isFinite(min)) quantity = Math.max(quantity, min);
          if (max !== null && Number.isFinite(max)) {
            quantity = Math.min(quantity, max);
          }

          quantity = Math.max(1, Math.floor(quantity));

          /*
            `available` is total sellable inventory for the selected variant.
            Subtract what is already in the cart before deciding whether this
            PDP can add more.
          */
          const remaining =
            available !== null && Number.isFinite(available)
              ? Math.max(0, Math.floor(available) - alreadyInCart)
              : null;

          if (remaining !== null && remaining <= 0 && alreadyInCart > 0) {
            quantityInput.value = '1';
            quantityInput.dispatchEvent(
              new Event('change', { bubbles: true })
            );

            productForm.dataset.watchersQuantityError = 'true';
            recoverProductForm(productForm, form);
            showError(productForm, 'Max qty already at the cart.');
            return;
          }

          if (
            remaining !== null &&
            Number.isFinite(remaining) &&
            quantity > remaining
          ) {
            const allowed = Math.max(0, remaining);

            quantityInput.value = String(allowed > 0 ? allowed : 1);
            quantityInput.dispatchEvent(
              new Event('change', { bubbles: true })
            );

            productForm.dataset.watchersQuantityError = 'true';
            recoverProductForm(productForm, form);

            if (allowed > 0) {
              showError(
                productForm,
                `Only ${allowed} ${allowed === 1 ? 'pc' : 'pcs'} Available`
              );
            } else if (alreadyInCart > 0) {
              showError(productForm, 'Max qty already at the cart.');
            } else {
              showError(productForm, 'Out of Stock');
            }

            return;
          }

          quantityInput.value = String(quantity);
          formData.set('quantity', String(quantity));
        }

        const sections = drawer
          .getSectionsToRender()
          .map((section) => section.id)
          .filter(Boolean);

        formData.set('sections', sections.join(','));
        formData.set('sections_url', window.location.pathname);

        drawer.setActiveElement?.(submitButton);

        const response = await fetch(
          window.routes?.cart_add_url || '/cart/add.js',
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
          }
        );

        const data = await response.json();

        if (!response.ok || data?.status) {
          const addError = new Error(
            data?.description ||
            data?.message ||
            'Unable to add this item.'
          );

          addError.status = Number(response.status || data?.status || 0);
          throw addError;
        }

        drawer.renderContents({
          ...data,
          watchersOpenDrawer: true,
        });

        await updateCartBubbleFromServer();

        document.dispatchEvent(
          new CustomEvent('watchers:cart:updated', {
            detail: {
              source: 'global-pdp-add-owner',
              productVariantId: formData.get('id'),
              cartData: data,
            },
          })
        );
      } catch (error) {
        console.error('[WATCHERS PDP Add Owner]', error);

        let message =
          error?.message || 'Unable to update the bag. Please try again.';

        if (Number(error?.status || 0) === 422) {
          try {
            const cart = await getCurrentCart();
            const formData = new FormData(form);
            const variantId =
              getExactLiveSelectedVariantId(
                form,
                formData.get('id') ||
                  variantInput.value ||
                  ''
              );
            const inCart = getVariantQuantityInCart(cart, variantId);

            if (inCart > 0) {
              message = 'Max qty already at the cart.';
            }
          } catch (_) {}
        }

        const quantityInput = getAssociatedQuantityInput(form);
        const retryFormData =
          new FormData(form);

        const retryVariantId =
          getExactLiveSelectedVariantId(
            form,
            retryFormData.get('id') ||
              variantInput.value ||
              ''
          );

        const available =
          getExactSelectedVariantInventory(
            form,
            retryVariantId,
            quantityInput
          );

        if (
          Number(error?.status) === 422 &&
          quantityInput &&
          available !== null &&
          Number.isFinite(available)
        ) {
          const allowed = Math.max(0, Math.floor(available));

          quantityInput.value = String(allowed > 0 ? allowed : 1);
          quantityInput.dispatchEvent(
            new Event('change', { bubbles: true })
          );

          message =
            allowed > 0
              ? `Only ${allowed} ${allowed === 1 ? 'pc' : 'pcs'} Available`
              : 'Out of Stock';
        }

        showError(productForm, message);

        if (
          Number(error?.status) === 422 ||
          /quantity|stock|inventory|available|maximum|only|can't add|cannot add/i.test(
            String(message)
          )
        ) {
          productForm.dataset.watchersQuantityError = 'true';
          recoverProductForm(productForm, form);
        }
      } finally {
        productForm.dataset.watchersSubmitting = 'false';
        setButtonBusy(productForm, false);
      }
    },
    true
  );

  /*
    CD23 — VARIANT SWITCH RESET

    A "Max qty" message belongs only to the variant that caused it.
    The moment the customer changes size/options, clear that stale message and
    restore ADD TO BAG while Dawn finishes its normal variant UI refresh.
  */
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;

      if (!(target instanceof HTMLElement)) return;

      if (
        !target.matches(
          'variant-selects select, ' +
          'variant-selects input[type="radio"], ' +
          'variant-selects fieldset input[type="radio"], ' +
          'fieldset input[type="radio"][data-option-value-id], ' +
          'select[name^="options"]'
        )
      ) {
        return;
      }

      const productInfo =
        target.closest('product-info');

      const productForm =
        productInfo?.querySelector(
          'product-form'
        );

      const form =
        productForm?.querySelector(
          'form[data-type="add-to-cart-form"]'
        );

      if (
        !productForm ||
        !(form instanceof HTMLFormElement)
      ) {
        return;
      }

      delete productForm.dataset
        .watchersQuantityError;

      showError(
        productForm,
        ''
      );

      recoverProductForm(
        productForm,
        form
      );
    },
    true
  );


  const recoverFromQuantityChange = (event) => {
    const input = event.target;

    if (!(input instanceof HTMLInputElement) || input.name !== 'quantity') {
      return;
    }

    const formId = input.getAttribute('form');
    const form =
      input.form ||
      (formId ? document.getElementById(formId) : input.closest('form'));

    if (!(form instanceof HTMLFormElement)) return;

    let productForm = form.closest('product-form');

    if (!productForm && form.id) {
      productForm = document
        .querySelector(`product-form form#${CSS.escape(form.id)}`)
        ?.closest('product-form');
    }

    if (!productForm) return;
    if (productForm.dataset.watchersQuantityError !== 'true') return;

    delete productForm.dataset.watchersQuantityError;
    showError(productForm, '');
    recoverProductForm(productForm, form);
  };

  document.addEventListener('input', recoverFromQuantityChange, true);
  document.addEventListener('change', recoverFromQuantityChange, true);
})();



/*
  CD15 — CART MINIMUM QUANTITY GUARD
  Quantity 1 is the floor. Removing a line is owned only by the trash button.
*/
(() => {
  if (window.__WATCHERS_CART_MIN_ONE__) return;
  window.__WATCHERS_CART_MIN_ONE__ = true;

  const clamp = (input) => {
    if (!input?.closest?.('cart-drawer')) return;
    const value = Number(input.value || 1);
    if (!Number.isFinite(value) || value < 1) input.value = '1';

    const minus = input.closest('quantity-input')?.querySelector('button[name="minus"]');
    if (minus) {
      const atMin = Number(input.value || 1) <= 1;
      minus.disabled = atMin;
      minus.toggleAttribute('aria-disabled', atMin);
    }
  };

  document.addEventListener('click', (event) => {
    const minus = event.target.closest?.('cart-drawer quantity-input button[name="minus"]');
    if (!minus) return;
    const input = minus.closest('quantity-input')?.querySelector('input[name="updates[]"]');
    if (Number(input?.value || 1) <= 1) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      clamp(input);
    }
  }, true);

  document.addEventListener('input', (event) => {
    if (event.target?.matches?.('cart-drawer quantity-input input[name="updates[]"]')) clamp(event.target);
  }, true);

  document.addEventListener('change', (event) => {
    if (event.target?.matches?.('cart-drawer quantity-input input[name="updates[]"]')) clamp(event.target);
  }, true);
})();


/*
  CD20 — QUEUED CONFIRMED MULTI-REMOVE

  Customer-facing behavior:
  - Every trash tap immediately changes THAT row to REMOVING...
  - Rows are NOT visually deleted before Shopify confirms.
  - Multiple taps are queued and committed to Shopify one-by-one.
  - This prevents two concurrent /cart/change.js responses from racing and
    resurrecting / retaining the second item.
  - After each authoritative drawer render, all still-queued keys are restored
    to REMOVING... until their own removal is confirmed.
  - The header count is synchronized from every confirmed Shopify response.
*/
(() => {
  if (window.__WATCHERS_QUEUED_CONFIRMED_REMOVE__) return;
  window.__WATCHERS_QUEUED_CONFIRMED_REMOVE__ = true;

  const pendingKeys = new Set();
  const removeQueue = [];
  let queueRunning = false;

  const getDrawer = () =>
    document.querySelector('cart-drawer.watchers-cart') ||
    document.querySelector('cart-drawer');

  const syncHeaderCartCount = (count) => {
    const trigger = document.querySelector('#cart-icon-bubble');
    if (!trigger) return;

    const safeCount = Math.max(0, Number(count || 0));
    let bubble = trigger.querySelector('.cart-count-bubble');

    if (safeCount <= 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      trigger.appendChild(bubble);
    }

    bubble.innerHTML =
      safeCount < 100
        ? `<span aria-hidden="true">${safeCount}</span><span class="visually-hidden">${safeCount} item${safeCount === 1 ? '' : 's'}</span>`
        : `<span class="visually-hidden">${safeCount} items</span>`;
  };

  const setRemovingUI = (row, button, removing) => {
    if (!row || !button) return;

    row.classList.toggle('is-watchers-remove-pending', removing);

    row.querySelectorAll(
      'quantity-input button, quantity-input input'
    ).forEach((control) => {
      if (removing) {
        control.setAttribute('disabled', 'disabled');
        control.setAttribute('aria-disabled', 'true');
      } else {
        control.removeAttribute('disabled');
        control.removeAttribute('aria-disabled');
      }
    });

    if (removing) {
      if (!button.dataset.watchersRemoveHtml) {
        button.dataset.watchersRemoveHtml = button.innerHTML;
      }

      button.setAttribute('aria-disabled', 'true');
      button.disabled = true;
      button.innerHTML = '<span>REMOVING...</span>';

      /*
        Preserve the exact trash-icon anchor. The R starts at the icon's
        visible left edge and the label grows only to the right.
      */
      button.style.setProperty('width', '38px', 'important');
      button.style.setProperty('min-width', '38px', 'important');
      button.style.setProperty('height', '38px', 'important');
      button.style.setProperty('padding', '0 0 0 10px', 'important');
      button.style.setProperty('margin', '0', 'important');
      button.style.setProperty('border', '0', 'important');
      button.style.setProperty('background', 'transparent', 'important');
      button.style.setProperty('box-shadow', 'none', 'important');
      button.style.setProperty('opacity', '1', 'important');
      button.style.setProperty(
        'color',
        'var(--watchers-cart-black, #111111)',
        'important'
      );
      button.style.setProperty('font', 'inherit', 'important');
      button.style.setProperty('font-size', '8px', 'important');
      button.style.setProperty('font-weight', '900', 'important');
      button.style.setProperty('line-height', '1', 'important');
      button.style.setProperty('letter-spacing', '0.06em', 'important');
      button.style.setProperty('text-transform', 'uppercase', 'important');
      button.style.setProperty('white-space', 'nowrap', 'important');
      button.style.setProperty('cursor', 'wait', 'important');
      button.style.setProperty('justify-content', 'flex-start', 'important');
      button.style.setProperty('text-align', 'left', 'important');
      button.style.setProperty('overflow', 'visible', 'important');
      button.style.setProperty('transform', 'none', 'important');
      button.style.setProperty('transition', 'none', 'important');
      button.style.setProperty('outline', 'none', 'important');
      button.style.setProperty(
        '-webkit-tap-highlight-color',
        'transparent',
        'important'
      );
      button.style.setProperty('user-select', 'none', 'important');
      button.style.setProperty('-webkit-user-select', 'none', 'important');

      try { button.blur(); } catch (_) {}
      try { window.getSelection()?.removeAllRanges(); } catch (_) {}
      return;
    }

    button.removeAttribute('aria-disabled');
    button.disabled = false;

    if (button.dataset.watchersRemoveHtml) {
      button.innerHTML = button.dataset.watchersRemoveHtml;
      delete button.dataset.watchersRemoveHtml;
    }

    [
      'width',
      'min-width',
      'height',
      'padding',
      'margin',
      'border',
      'background',
      'box-shadow',
      'opacity',
      'color',
      'font',
      'font-size',
      'font-weight',
      'line-height',
      'letter-spacing',
      'text-transform',
      'white-space',
      'cursor',
      'justify-content',
      'text-align',
      'overflow',
      'transform',
      'transition',
      'outline',
      '-webkit-tap-highlight-color',
      'user-select',
      '-webkit-user-select',
    ].forEach((property) => button.style.removeProperty(property));
  };

  const getLiveRemoveParts = (key) => {
    const owner = Array.from(
      document.querySelectorAll(
        'cart-drawer cart-remove-button[data-line-key]'
      )
    ).find((node) => node.dataset.lineKey === key);

    if (!owner) {
      return { owner: null, button: null, row: null };
    }

    return {
      owner,
      button: owner.querySelector('button'),
      row: owner.closest(
        '.watchers-cart__item[data-line-key], .cart-item[data-line-key]'
      ),
    };
  };

  const reapplyPendingRemovingStates = () => {
    pendingKeys.forEach((key) => {
      const { row, button } = getLiveRemoveParts(key);
      if (row && button) {
        setRemovingUI(row, button, true);
      }
    });
  };

  const showRemoveError = (message) => {
    const errorBox = document.querySelector('#CartDrawer-CartErrors');
    if (errorBox) {
      errorBox.textContent =
        message || 'Unable to remove item. Please try again.';
    }
  };

  const processRemoveQueue = async () => {
    if (queueRunning) return;
    queueRunning = true;

    try {
      while (removeQueue.length) {
        const key = removeQueue[0];

        /*
          A previous render may have replaced the drawer DOM. Re-assert every
          queued/pending label before starting this mutation.
        */
        reapplyPendingRemovingStates();

        const drawer = getDrawer();
        const sections =
          drawer && typeof drawer.getSectionsToRender === 'function'
            ? drawer
                .getSectionsToRender()
                .map((item) => item.id)
                .filter(Boolean)
            : ['cart-drawer', 'cart-icon-bubble'];

        try {
          const response = await fetch(
            window.routes?.cart_change_url || '/cart/change.js',
            {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              cache: 'no-store',
              body: JSON.stringify({
                id: key,
                quantity: 0,
                sections: sections.join(','),
                sections_url: window.location.pathname,
              }),
            }
          );

          const data = await response.json();

          if (!response.ok || data?.status) {
            throw new Error(
              data?.description ||
              data?.message ||
              'Unable to remove item.'
            );
          }

          /*
            This key is now really removed on Shopify.
            Remove it from the pending set BEFORE rendering so it does not get
            re-applied to a row that should disappear.
          */
          pendingKeys.delete(key);
          removeQueue.shift();

          syncHeaderCartCount(data?.item_count);

          if (
            drawer &&
            typeof drawer.renderContents === 'function' &&
            data?.sections
          ) {
            drawer.renderContents({
              ...data,
              watchersOpenDrawer: false,
            });

            syncHeaderCartCount(data?.item_count);

            /*
              CRITICAL MULTI-DELETE RULE:
              the fresh drawer must immediately restore REMOVING... for every
              queued key that has not yet reached Shopify.
            */
            reapplyPendingRemovingStates();
          }

          document.dispatchEvent(
            new CustomEvent('watchers:cart:updated', {
              detail: {
                source: 'confirmed-remove',
                itemCount: Number(data?.item_count || 0),
              },
            })
          );
        } catch (error) {
          console.error('[WATCHERS Queued Remove]', error);

          /*
            Only this failed key leaves the pending queue. Other queued keys
            remain REMOVING... and continue normally.
          */
          pendingKeys.delete(key);
          removeQueue.shift();

          const { row, button } = getLiveRemoveParts(key);
          if (row && button) {
            setRemovingUI(row, button, false);
          }

          showRemoveError(error?.message);
          reapplyPendingRemovingStates();
        }
      }
    } finally {
      queueRunning = false;

      /*
        A tap can arrive in the tiny interval between the while loop becoming
        empty and queueRunning being cleared.
      */
      if (removeQueue.length) {
        processRemoveQueue();
      }
    }
  };

  /*
    Any WATCHERS cart render can replace the drawer DOM. Pending queued keys
    must never revert visually to trash icons.
  */
  document.addEventListener(
    'watchers:cart:rendered',
    reapplyPendingRemovingStates
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.(
        'cart-drawer cart-remove-button[data-line-key] button'
      );

      if (!button) return;

      const owner = button.closest(
        'cart-remove-button[data-line-key]'
      );
      const key = owner?.dataset.lineKey;

      if (!key || pendingKeys.has(key)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const row = owner.closest(
        '.watchers-cart__item[data-line-key], .cart-item[data-line-key]'
      );

      /*
        Mark immediately, so item 2 can display REMOVING... while item 1 is
        still being committed. Backend requests themselves remain serial.
      */
      pendingKeys.add(key);
      removeQueue.push(key);
      setRemovingUI(row, button, true);

      processRemoveQueue();
    },
    true
  );
})();
